import {createContext, useCallback, useRef, useState} from "react";
import {
    getLocalStorageCurrentIdentity,
    getLocalStorageCurrentProjectId,
    getLocalStorageCurrentProjectName,
    setLocalStorageCurrentIdentity,
    setLocalStorageCurrentProjectId,
    setLocalStorageCurrentProjectName
} from "../localStorage.js";

import {Configuration as AuthorityConfiguration, ProjectApi} from "../../api/authority/generated/index";
import {createSessionAuthenticationMiddleware} from "../../api/utilities/sessionAuthentication.js";

export const GlobalContext = createContext(null);

export default function GlobalContextProvider({children}) {
    const invalidatedAuthenticationToken = useRef(null);
    const contextRef = useRef(null);
    const setCurrentIdentityRef = useRef(null);
    // TODO: any type would be great...
    let [context, setContextState] = useState({
        userDropdownOpened: false,
        currentIdentity: getLocalStorageCurrentIdentity(),
        currentProjectId: getLocalStorageCurrentProjectId(),
        currentProjectName: getLocalStorageCurrentProjectName(),
        currentUserProjects: [],
        currentUserProjectsLoaded: false,
        currentUserProjectsReload: true,
        currentUserSettings: null,
        currentUserSettingsLoaded: false,
        currentUserSettingsLoading: false,
        globalAlerts: [],
        defaultSubscriptionValues: null,
        defaultSubscriptionValuesLoaded: false,
        supportedCurrencies: null,
        supportedCurrenciesLoaded: false,
        localizationLoaded: false,
        permissions: [],
        permissionsLoaded: false,
    });
    contextRef.current = context;

    let setCurrentUserProjects = (value) => {

        let newCurrentProjectId = null;

        for (let possibleSelectedProject of value) {
            if (possibleSelectedProject.id === context.currentProjectId) {
                newCurrentProjectId = possibleSelectedProject.id;
            }
        }

        setContextState((context) => {
            return {...context, currentUserProjects: value, currentProjectId: newCurrentProjectId};
        })
    };

    let setUserSettings = (userSettings) => {
        setContextState((context) => {
            return {
                ...context,
                currentUserSettings: userSettings,
                currentUserSettingsLoaded: true,
                currentUserSettingsLoading: false,
            };
        });
    };

    let setUserSettingsLoading = (value) => {
        setContextState((context) => {
            return {...context, currentUserSettingsLoading: value};
        });
    };

    let resetCurrentUserProjects = () => {
        setCurrentUserProjects([]);
        setCurrentUserProjectsLoaded(false);
        setCurrentUserProjectsReload(true);
    };

    let addCurrentUserProject = (value, forToken) => {
        setContextState((context) => {
            let currentToken = context.currentIdentity?.token ?? null;
            if (currentToken !== forToken) {
                return context;
            }

            let existingIndex = null;

            for (let projectIndex in context.currentUserProjects) {
                let existingProject = context.currentUserProjects[projectIndex];
                if (existingProject.id === value.id) {
                    existingIndex = projectIndex;
                }
            }

            if(existingIndex) {
                context.currentUserProjects[existingIndex] = value;
            } else {
                context.currentUserProjects.push(value);
            }

            return context;
        });
    };

    let setCurrentUserProjectsLoaded = (value, forToken) => {
        setContextState((context) => {
            let currentToken = context.currentIdentity?.token ?? null;

            if (currentToken !== forToken) {
                return context;
            }

            if (value) {
                let possibleProjectId = context.currentUserProjects[0]?.id;
                if (possibleProjectId !== null && possibleProjectId !== undefined) {
                    context.currentProjectId = possibleProjectId;
                }
            }

            return {...context, currentUserProjectsLoaded: value};
        })
    };

    let setCurrentUserProjectsReload = (value) => {
        setContextState((context) => {
            return {...context, currentUserProjectsReload: value};
        })
    };

    let setDefaultSubscriptionValuesLoaded = () => {
        setContextState((context) => {
            return {...context, defaultSubscriptionValuesLoaded: true};
        })
    };

    let setDefaultSubscriptionValues = (value) => {
        setContextState((context) => {
            return {...context, defaultSubscriptionValues: value};
        })
    };

    let setSupportedCurrenciesLoaded = () => {
        setContextState((context) => {
            return {...context, supportedCurrenciesLoaded: true};
        })
    };

    let setSupportedCurrencies = (value) => {
        setContextState((context) => {
            return {...context, supportedCurrencies: value};
        })
    };

    let setCurrentProjectId = (value) => {
        setLocalStorageCurrentProjectId(value);
        let projectName = null;

        // Move project with this id on the first place...
        let currentProjectIndexInTheList = null;
        for (let projectIndex in context.currentUserProjects) {
            let project = context.currentUserProjects[projectIndex];

            if (project.id === value) {
                currentProjectIndexInTheList = projectIndex;

                projectName = project.name;
            }
        }

        setLocalStorageCurrentProjectName(projectName);

        if (currentProjectIndexInTheList !== null) {
            context.currentUserProjects.unshift(
                context.currentUserProjects.splice(currentProjectIndexInTheList, 1)[0]
            );
        }

        if (value) {
            new ProjectApi(getAuthorityConfiguration())
                .touchProject({
                    projectId: value,
                })
                .then(() => {
                })
                .catch(() => {
                });
        }

        setContextState((context) => {
            return {
                ...context,
                currentProjectId: value,
                currentProjectName: projectName,
                currentUserProjects: context.currentUserProjects
            };
        });
    };

    let setCurrentIdentity = (value, remember = true) => {
        setLocalStorageCurrentIdentity(value, remember);
        setCurrentProjectId(null);
        setUserDropdownOpened(false);
        resetCurrentUserProjects();
        setContextState((context) => {
            return {
                ...context,
                currentIdentity: value,
                currentUserSettings: null,
                currentUserSettingsLoaded: false,
                currentUserSettingsLoading: false,
            };
        });
    };
    setCurrentIdentityRef.current = setCurrentIdentity;

    let updateIdentityUser = (user, after = () => {
    }) => {
        setContextState((context) => {

            let identity = context.currentIdentity;

            if (user.id === identity.user.id) {
                identity.user = user;
                setLocalStorageCurrentIdentity(identity);
            }

            return {...context, currentIdentity: identity};
        });
    };

    let setLocalizationLoaded = (value) => {
        setContextState((context) => {
            return {...context, localizationLoaded: value};
        })
    };

    let setUserDropdownOpened = (value) => {
        setContextState((context) => {
            return {...context, userDropdownOpened: value};
        })
    };

    const getAuthorityConfiguration = useCallback(() => {
        let headers = {};
        let authenticationToken = contextRef.current?.currentIdentity?.token;

        if (authenticationToken) {
            headers['authorization'] = 'Bearer ' + authenticationToken;
        }

        return new AuthorityConfiguration({
            basePath: import.meta.env.VITE_AUTHORITY_URL,
            headers: headers,
            middleware: authenticationToken ? [createSessionAuthenticationMiddleware(
                authenticationToken,
                (invalidToken) => {
                    if (invalidatedAuthenticationToken.current === invalidToken) {
                        return;
                    }

                    invalidatedAuthenticationToken.current = invalidToken;
                    setCurrentIdentityRef.current?.(null);
                },
            )] : [],
        });
    }, []);

    let setPermissions = (permissions) => {
        setContextState((context) => {
            return {
                ...context,
                permissions: permissions,
                permissionsLoaded: true,
            };
        });
    };

    let setGlobalAlerts = (alerts) => {
        setContextState((context) => {
            return {
                ...context,
                globalAlerts: alerts,
            };
        });
    };

    return (
        <GlobalContext.Provider value={{
            context: context,
            setCurrentIdentity: setCurrentIdentity,
            updateIdentityUser: updateIdentityUser,
            setCurrentProjectId: setCurrentProjectId,
            setCurrentUserProjects: setCurrentUserProjects,
            resetCurrentUserProjects: resetCurrentUserProjects,
            setUserSettings: setUserSettings,
            setUserSettingsLoading: setUserSettingsLoading,
            addCurrentUserProject: addCurrentUserProject,
            setCurrentUserProjectsLoaded: setCurrentUserProjectsLoaded,
            setCurrentUserProjectsReload: setCurrentUserProjectsReload,
            setLocalizationLoaded: setLocalizationLoaded,
            setUserDropdownOpened: setUserDropdownOpened,
            getAuthorityConfiguration: getAuthorityConfiguration,
            setPermissions: setPermissions,
            setGlobalAlerts: setGlobalAlerts,
            setDefaultSubscriptionValues: setDefaultSubscriptionValues,
            setDefaultSubscriptionValuesLoaded: setDefaultSubscriptionValuesLoaded,
            setSupportedCurrencies: setSupportedCurrencies,
            setSupportedCurrenciesLoaded: setSupportedCurrenciesLoaded,
        }}>
            {children}
        </GlobalContext.Provider>
    );
}
